<template>
  <ion-modal :is-open="isIncidentModalOpen">
    <ion-content>
      <ModalHeader
        :title="incident && step > 0 ? (incident.name || '') : 'Anmäl'"
        :step="step"
        @on-close="onClose"
      />
      <IncidentProgressBar
        v-if="active"
        :step="step"
        :scrolled="scrolled"
        :has-optional-fields="hasOptionalFields"
      />
      <div v-if="incidentLoading" class="absolute inset-0 flex items-center justify-center pt-32 bg-darker opacity-50 z-10">
        <ion-spinner
          class="w-56 h-56 text-white"
          aria-hidden="true"
        />
      </div>
      <swiper
        class="swiper-fullscreen"
        :slides-per-view="1"
        :allow-touch-move="false"
        @swiper="setSwiperInstance"
      >
        <swiper-slide>
          <Step1
            class="incident-page"
          />
        </swiper-slide>
        <swiper-slide v-if="active && incident">
          <Step2
            class="incident-page"
            @set-scrolled="setScrolled"
          />
        </swiper-slide>
        <swiper-slide v-if="hasOptionalFields && active && incident">
          <Step3
            class="incident-page"
            @set-scrolled="setScrolled"
          />
        </swiper-slide>
        <swiper-slide v-if="active && incident">
          <Step4
            class="incident-page"
            @set-scrolled="setScrolled"
          />
        </swiper-slide>
        <swiper-slide v-if="incident">
          <Summary
            class="incident-page"
          />
        </swiper-slide>
      </swiper>
    </ion-content>
  </ion-modal>
</template>

<script setup lang="ts">
import { IonModal, IonContent, IonSpinner } from '@ionic/vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import Step1 from '@/components/incident-modal/Step1.vue';
import Step2 from '@/components/incident-modal/Step2.vue';
import Step3 from '@/components/incident-modal/Step3.vue';
import Step4 from '@/components/incident-modal/Step4.vue';
import Summary from '@/components/incident-modal/Summary.vue';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import 'swiper/css';
import '@ionic/vue/css/ionic-swiper.css';
import { useIncidentStore } from '@/store/incident';
import ModalHeader from '@/components/incident-modal/IncidentModalHeader.vue';
import IncidentProgressBar from '@/components/incident-modal/IncidentProgressBar.vue';
import IncidentStatus from '@/constants/incident-status';

const incidentStore = useIncidentStore();
const { incident, incidentLoading, hasOptionalFields, step, isIncidentModalOpen, pendingIncidentId } = storeToRefs(incidentStore);

// const { isIncidentModalOpen } = storeToRefs(uiStore);

const swiperRef = ref();
const pendingSlide = ref<number | null>(null);
const scrolled = ref(false);

const canSlide = (swiper: any) => {
  return !!swiper && !swiper.destroyed && typeof swiper.slideTo === 'function';
};

const setSwiperInstance = (swiper: any) => {
  swiperRef.value = swiper;
  if (pendingSlide.value !== null && canSlide(swiperRef.value)) {
    swiperRef.value.slideTo(pendingSlide.value);
    pendingSlide.value = null;
  }
};

const onClose = async() => {
  if (incidentStore.editingCompletedIncident) {
    incidentStore.discardEditingCompletedIncident();
    return;
  }
  const isEditable = incident.value && (incident.value.status !== IncidentStatus.Completed || incidentStore.editingCompletedIncident);
  if (isEditable) {
    await incidentStore.leaveIncidentReport();
  } else {
    incidentStore.closeIncidentModal();
  }
};

const setScrolled = (isScrolled: boolean) => {
  scrolled.value = isScrolled;
};

const active  = computed(() => {
  return incident.value && (incident.value.status !== IncidentStatus.Completed || incidentStore.editingCompletedIncident);
});

watch(
  step,
  () => {
    setScrolled(false);
    if (!isIncidentModalOpen.value || step.value === null) {
      pendingSlide.value = step.value;
      return;
    }
    if (canSlide(swiperRef.value)) {
      swiperRef.value.slideTo(step.value);
    } else {
      pendingSlide.value = step.value;
    }
  }
);

const loadPendingIncident = async() => {
  const id = pendingIncidentId.value;
  if (!id || !isIncidentModalOpen.value) {
    return;
  }

  incidentStore.setPendingIncidentId(null);

  const found = await incidentStore.fetchIncident({ incidentId: id });
  if (found) {
    incidentStore.onSet(4);
  } else {
    incidentStore.onSet(0);
    await incidentStore.fetchUserIncidents();
    incidentStore.setOpenHistoryTab(true);
  }
};

watch(
  [isIncidentModalOpen, pendingIncidentId],
  () => {
    loadPendingIncident();
  }
);

</script>
<style scoped>
.incident-page {
  height: 100%;
  width: 100%;
}

ion-modal {
  --width: 100%;
  --height: 100%;
}
</style>
